import { Component, EventEmitter, Input, Output, OnInit, OnChanges, OnDestroy, SimpleChanges, ViewChild, ElementRef } from '@angular/core';
import { FormBuilder, FormGroup, FormControl } from '@angular/forms';
import { Subject, Observable, of, forkJoin, merge } from 'rxjs';
import { distinctUntilChanged, debounceTime, switchMap, catchError, startWith, map, takeUntil } from 'rxjs/operators';
import { DepartmentType, StatusType } from '../../models/request.model';
import { FilterMenuItem, FilterOptions, SavedFilter } from '../filter-menu/filter-menu.component';
import { UserService } from 'src/app/user/service/user.abstract.service';
import { UserResponse } from 'src/app/user/user.model';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { MatChipInputEvent } from '@angular/material/chips';
import { RequestsV2ImplService } from '../../services/requests-v2.impl.service';

import { DateAdapter, NativeDateAdapter } from '@angular/material/core';
import { TranslateService, LangChangeEvent } from '@ngx-translate/core';
import { Injectable } from '@angular/core';

@Injectable()
export class CustomDateAdapter extends NativeDateAdapter {
    format(date: Date, displayFormat: Object): string {
        if (this.locale === 'fr-FR' || this.locale === 'fr') {
            // If the format includes day, we assume it's a full date and format it as dd-mm-yyyy
            if ((displayFormat as any).day) {
                const day = date.getDate().toString().padStart(2, '0');
                const month = (date.getMonth() + 1).toString().padStart(2, '0');
                const year = date.getFullYear();
                return `${day}-${month}-${year}`;
            }
        }
        return super.format(date, displayFormat);
    }
}

@Component({
    selector: 'app-request-filter-sidenav',
    templateUrl: './request-filter-sidenav.component.html',
    styleUrls: ['./request-filter-sidenav.component.scss'],
    providers: [{ provide: DateAdapter, useClass: CustomDateAdapter }],
})
export class RequestFilterSidenavComponent implements OnInit, OnChanges, OnDestroy {
    private destroy$ = new Subject<void>();
    private tagInputFocus$ = new Subject<string>();
    @Input() departmentTypes: DepartmentType[] = [];
    @Input() categories: FilterMenuItem[] = [];
    @Input() subcategories: FilterMenuItem[] = [];
    @Input() statusTypes: StatusType[] = [];
    @Input() users: FilterMenuItem[] = [];
    @Input() currentFilters: FilterOptions;

    @Output() filterChanged = new EventEmitter<FilterOptions>();
    @Output() close = new EventEmitter<void>();

    @Input() savedFilters: SavedFilter[] = [];
    @Output() saveFilter = new EventEmitter<{ name: string; filters: FilterOptions }>();
    @Output() deleteFilter = new EventEmitter<string>();
    @Output() loadFilter = new EventEmitter<SavedFilter>();

    filterForm: FormGroup;
    filteredSubcategories: FilterMenuItem[] = [];
    private _triggerFilterChange = new Subject<void>();

    requesterSearchCtrl = new FormControl();
    assigneeSearchCtrl = new FormControl();
    filteredRequesters$: Observable<UserResponse[]>;
    filteredAssignees$: Observable<UserResponse[]>;
    selectedRequesters: UserResponse[] = [];
    selectedAssignees: UserResponse[] = [];
    maxDate: Date = new Date();
    private readonly multiSelectFields = ['status', 'departments', 'categories', 'subCategory'] as const;

    tagSearchCtrl = new FormControl('');
    selectedTags: string[] = [];
    filteredTagSuggestions: string[] = [];
    tagsFilterMode: 'AND' | 'OR' = 'OR';
    separatorKeyCodes: number[] = [ENTER, COMMA];

    isSaving = false;
    isAccordionOpen = false;
    filterNameControl = new FormControl('');

    @ViewChild('filterNameInput') filterNameInput: ElementRef<HTMLInputElement>;

    @ViewChild('requesterInput') requesterInput: ElementRef<HTMLInputElement>;
    @ViewChild('assigneeInput') assigneeInput: ElementRef<HTMLInputElement>;

    constructor(
        private fb: FormBuilder,
        private userService: UserService,
        private dateAdapter: DateAdapter<any>,
        private translate: TranslateService,
        private requestService: RequestsV2ImplService,
    ) {
        this.filterForm = this.fb.group({
            requester: [''],
            status: [[]],
            createdAtFrom: [null],
            createdAtTo: [null],
            slaDate: [null],
            departments: [[]],
            categories: [[]],
            subCategory: [[]],
            assignee: [''],
        });
    }

    ngOnInit(): void {
        this.setLocale(this.translate.currentLang);
        this.translate.onLangChange.pipe(takeUntil(this.destroy$)).subscribe((event: LangChangeEvent) => {
            this.setLocale(event.lang);
        });

        if (this.currentFilters) {
            this.patchFormWithFilters(this.currentFilters);
        }

        this._triggerFilterChange.pipe(debounceTime(100), takeUntil(this.destroy$)).subscribe(() => {
            this.applyFilters();
        });

        // Auto-apply filters when dates change
        this.filterForm
            .get('createdAtFrom')
            ?.valueChanges.pipe(distinctUntilChanged(), takeUntil(this.destroy$))
            .subscribe((val) => {
                const toDate = this.filterForm.get('createdAtTo')?.value;
                if (val && !toDate) {
                    return;
                }
                this._triggerFilterChange.next();
            });

        this.filterForm
            .get('createdAtTo')
            ?.valueChanges.pipe(distinctUntilChanged(), takeUntil(this.destroy$))
            .subscribe(() => {
                this._triggerFilterChange.next();
            });

        this.filterForm
            .get('slaDate')
            ?.valueChanges.pipe(takeUntil(this.destroy$))
            .subscribe(() => {
                this._triggerFilterChange.next();
            });

        this.filteredRequesters$ = this.requesterSearchCtrl.valueChanges.pipe(
            startWith(''),
            debounceTime(300),
            distinctUntilChanged(),
            switchMap((value) => {
                const query = typeof value === 'string' ? value : value?.firstName;
                if (query && query.length >= 1) {
                    return this.userService.quickSearch(query, 1, 10).pipe(
                        map((result) => (result ? result.docs : [])),
                        catchError(() => of([])),
                    );
                } else {
                    return of([]);
                }
            }),
        );

        this.filteredAssignees$ = this.assigneeSearchCtrl.valueChanges.pipe(
            startWith(''),
            debounceTime(300),
            distinctUntilChanged(),
            switchMap((value) => {
                const query = typeof value === 'string' ? value : value?.firstName;
                if (query && query.length >= 1) {
                    return this.userService.quickSearch(query, 1, 10).pipe(
                        map((result) => (result ? result.docs : [])),
                        catchError(() => of([])),
                    );
                } else {
                    return of([]);
                }
            }),
        );

        this.filterForm
            .get('categories')
            ?.valueChanges.pipe(takeUntil(this.destroy$))
            .subscribe(() => {
                this.filterSubcategories();
                this._triggerFilterChange.next();
            });

        this.filterForm
            .get('status')
            ?.valueChanges.pipe(takeUntil(this.destroy$))
            .subscribe(() => {
                this._triggerFilterChange.next();
            });

        this.filterForm
            .get('departments')
            ?.valueChanges.pipe(takeUntil(this.destroy$))
            .subscribe(() => {
                this._triggerFilterChange.next();
            });

        this.filterForm
            .get('subCategory')
            ?.valueChanges.pipe(takeUntil(this.destroy$))
            .subscribe(() => {
                this._triggerFilterChange.next();
            });

        merge(this.tagSearchCtrl.valueChanges.pipe(debounceTime(300), distinctUntilChanged()), this.tagInputFocus$)
            .pipe(
                switchMap((value) => {
                    if (value === null || value === undefined) return of([]);
                    return this.requestService.searchTags(value).pipe(catchError(() => of([])));
                }),
                takeUntil(this.destroy$),
            )
            .subscribe((tags: string[]) => {
                this.filteredTagSuggestions = tags.filter((t) => !this.selectedTags.includes(t));
            });
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.currentFilters && changes.currentFilters.currentValue) {
            this.patchFormWithFilters(changes.currentFilters.currentValue);
        }
        if (changes.subcategories) {
            this.filterSubcategories();
        }
    }

    filterSubcategories(): void {
        const selectedCategoryIds = this.getSelectedValues('categories');
        if (selectedCategoryIds.length > 0) {
            this.filteredSubcategories = this.subcategories.filter((sub) => selectedCategoryIds.includes(sub.categoryId || ''));
        } else {
            this.filteredSubcategories = [...this.subcategories];
        }

        const availableSubcategoryIds = new Set(this.filteredSubcategories.map((sub) => sub.id));
        const selectedSubcategories = this.getSelectedValues('subCategory');
        const validSubcategories = selectedSubcategories.filter((id) => availableSubcategoryIds.has(id));

        if (validSubcategories.length !== selectedSubcategories.length) {
            this.filterForm.get('subCategory')?.setValue(validSubcategories, { emitEvent: false });
            this._triggerFilterChange.next();
        }
    }

    private toArray(value: string | string[] | null | undefined): string[] {
        if (!value) {
            return [];
        }

        if (Array.isArray(value)) {
            return value;
        }

        return value
            .split(',')
            .map((item) => item.trim())
            .filter(Boolean);
    }

    private firstOrNull(value: string | string[] | null | undefined): string | null {
        const values = this.toArray(value);
        return values.length > 0 ? values[0] : null;
    }

    patchFormWithFilters(filters: FilterOptions): void {
        // Map existing filters to the new form structure
        this.filterForm.patchValue(
            {
                requester: this.firstOrNull(filters.requester),
                status: this.toArray(filters.status),
                createdAtFrom: filters.createdAtFrom,
                createdAtTo: filters.createdAtTo,
                slaDate: filters.slaDate,
                departments: this.toArray(filters.department),
                categories: this.toArray(filters.category),
                subCategory: this.toArray(filters.subCategory),
                assignee: this.firstOrNull(filters.assignee),
            },
            { emitEvent: false },
        );

        if (filters.requester) {
            this.loadUsersFromIds(this.toArray(filters.requester), 'requester');
        }
        if (filters.assignee) {
            this.loadUsersFromIds(this.toArray(filters.assignee), 'assignee');
        }
        if (filters.tags) {
            this.selectedTags = this.toArray(filters.tags);
        }
        if (filters.tagsFilterMode) {
            this.tagsFilterMode = filters.tagsFilterMode;
        }

        this.filterSubcategories();
    }

    loadUsersFromIds(ids: string[], type: 'requester' | 'assignee'): void {
        const requests = ids.map((id) => this.userService.getProfileById(id).pipe(catchError(() => of(null))));
        forkJoin(requests)
            .pipe(takeUntil(this.destroy$))
            .subscribe((users) => {
                const validUsers = users.filter((u) => u !== null) as UserResponse[];
                if (type === 'requester') {
                    this.selectedRequesters = validUsers;
                } else {
                    this.selectedAssignees = validUsers;
                }
            });
    }

    applyFilters(): void {
        const formValue = this.filterForm.value;

        const filters: any = {
            department: this.getSelectedValues('departments').join(','),
            category: this.getSelectedValues('categories').join(','),
            subCategory: this.getSelectedValues('subCategory').join(','),
            status: this.getSelectedStatuses().join(','),
            requester: this.selectedRequesters.map((u) => u._id).join(','),
            createdAtFrom: this.filterForm.get('createdAtFrom')?.value,
            createdAtTo: this.filterForm.get('createdAtTo')?.value,
            slaDate: this.filterForm.get('slaDate')?.value,
            assignee: this.selectedAssignees.map((u) => u._id).join(','),
            tags: this.selectedTags.join(','),
            tagsFilterMode: this.tagsFilterMode,
        };

        if (this.currentFilters && this.areFiltersEqual(filters, this.currentFilters)) {
            return;
        }

        this.filterChanged.emit(filters);
    }

    private areFiltersEqual(newFilters: any, currentFilters: FilterOptions): boolean {
        const normalize = (val: any) => {
            if (val === null || val === undefined || val === '') return null;
            if (Array.isArray(val) && val.length === 0) return null;
            return val;
        };
        const normalizeMultiValue = (val: string | string[] | null | undefined) => {
            const values = this.toArray(val);
            return values.length ? [...values].sort().join(',') : null;
        };

        if (normalizeMultiValue(newFilters.category) !== normalizeMultiValue(currentFilters.category)) return false;
        if (normalizeMultiValue(newFilters.subCategory) !== normalizeMultiValue(currentFilters.subCategory)) return false;
        if (normalizeMultiValue(newFilters.status) !== normalizeMultiValue(currentFilters.status)) return false;
        if (normalizeMultiValue(newFilters.department) !== normalizeMultiValue(currentFilters.department)) return false;
        if (normalize(newFilters.requester) !== normalize(currentFilters.requester)) return false;
        if (normalize(newFilters.assignee) !== normalize(currentFilters.assignee)) return false;

        const getTime = (d: any) => {
            if (!d) return 0;
            const date = new Date(d);
            return isNaN(date.getTime()) ? 0 : date.getTime();
        };

        if (getTime(newFilters.createdAtFrom) !== getTime(currentFilters.createdAtFrom)) return false;
        if (getTime(newFilters.createdAtTo) !== getTime(currentFilters.createdAtTo)) return false;
        if (getTime(newFilters.slaDate) !== getTime(currentFilters.slaDate)) return false;
        if (normalizeMultiValue(newFilters.tags) !== normalizeMultiValue(currentFilters.tags)) return false;
        if (newFilters.tagsFilterMode !== currentFilters.tagsFilterMode) return false;

        return true;
    }

    onClose(): void {
        this.close.emit();
    }

    reset(): void {
        this.filterForm.reset({
            requester: '',
            status: [],
            createdAtFrom: null,
            createdAtTo: null,
            slaDate: null,
            departments: [],
            categories: [],
            subCategory: [],
            assignee: '',
        });
        this.selectedRequesters = [];
        this.selectedAssignees = [];
        this.selectedTags = [];
        this.tagsFilterMode = 'OR';
        this.requesterSearchCtrl.setValue(null);
        this.assigneeSearchCtrl.setValue(null);
        this.tagSearchCtrl.setValue(null, { emitEvent: false });
        this._triggerFilterChange.next();
    }

    hasActiveFilters(): boolean {
        const formValue = this.filterForm.value;
        return (
            this.selectedRequesters.length > 0 ||
            this.selectedAssignees.length > 0 ||
            this.selectedTags.length > 0 ||
            this.getSelectedStatuses().length > 0 ||
            this.getSelectedValues('departments').length > 0 ||
            this.getSelectedValues('categories').length > 0 ||
            this.getSelectedValues('subCategory').length > 0 ||
            !!formValue.createdAtFrom ||
            !!formValue.createdAtTo ||
            !!formValue.slaDate
        );
    }

    clearField(fieldName: string): void {
        if (fieldName === 'requester') {
            this.filterForm.get('requester')?.setValue('');
            this.selectedRequesters = [];
            this.requesterSearchCtrl.setValue(null);
        } else if (fieldName === 'assignee') {
            this.filterForm.get('assignee')?.setValue('');
            this.selectedAssignees = [];
            this.assigneeSearchCtrl.setValue(null);
        } else if (fieldName === 'tags') {
            this.selectedTags = [];
            this.tagsFilterMode = 'OR';
            this.tagSearchCtrl.setValue(null, { emitEvent: false });
        } else if (fieldName === 'creationDate') {
            this.filterForm.get('createdAtFrom')?.setValue(null);
            this.filterForm.get('createdAtTo')?.setValue(null);
        } else if (this.isMultiSelectField(fieldName)) {
            this.filterForm.get(fieldName)?.setValue([]);
        } else {
            this.filterForm.get(fieldName)?.setValue(null);
        }
        this._triggerFilterChange.next();
    }

    getSelectedStatuses(): string[] {
        return this.toArray(this.filterForm.get('status')?.value);
    }

    getSelectedValues(fieldName: 'status' | 'departments' | 'categories' | 'subCategory'): string[] {
        return this.toArray(this.filterForm.get(fieldName)?.value);
    }

    isStatusSelected(statusId: string): boolean {
        return this.getSelectedStatuses().includes(statusId);
    }

    isValueSelected(fieldName: 'departments' | 'categories' | 'subCategory', value: string): boolean {
        return this.getSelectedValues(fieldName).includes(value);
    }

    toggleStatus(statusId: string, checked: boolean): void {
        const selectedStatuses = this.getSelectedStatuses();
        const nextStatuses = checked ? [...selectedStatuses, statusId] : selectedStatuses.filter((id) => id !== statusId);
        this.filterForm.get('status')?.setValue(nextStatuses);
    }

    toggleSelection(fieldName: 'departments' | 'categories' | 'subCategory', value: string, checked: boolean): void {
        const selectedValues = this.getSelectedValues(fieldName);
        const nextValues = checked ? [...selectedValues, value] : selectedValues.filter((id) => id !== value);
        this.filterForm.get(fieldName)?.setValue(nextValues);
    }

    private isMultiSelectField(fieldName: string): fieldName is (typeof this.multiSelectFields)[number] {
        return this.multiSelectFields.includes(fieldName as (typeof this.multiSelectFields)[number]);
    }

    removeRequester(user: UserResponse): void {
        const index = this.selectedRequesters.indexOf(user);
        if (index >= 0) {
            this.selectedRequesters.splice(index, 1);
            this._triggerFilterChange.next();
        }
    }

    removeAssignee(user: UserResponse): void {
        const index = this.selectedAssignees.indexOf(user);
        if (index >= 0) {
            this.selectedAssignees.splice(index, 1);
            this._triggerFilterChange.next();
        }
    }

    selectRequester(event: MatAutocompleteSelectedEvent): void {
        const user = event.option.value as UserResponse;
        if (!this.selectedRequesters.find((u) => u._id === user._id)) {
            this.selectedRequesters.push(user);
            this._triggerFilterChange.next();
        }
        if (this.requesterInput) {
            this.requesterInput.nativeElement.value = '';
        }
        this.requesterSearchCtrl.setValue(null);
    }

    selectAssignee(event: MatAutocompleteSelectedEvent): void {
        const user = event.option.value as UserResponse;
        if (!this.selectedAssignees.find((u) => u._id === user._id)) {
            this.selectedAssignees.push(user);
            this._triggerFilterChange.next();
        }
        if (this.assigneeInput) {
            this.assigneeInput.nativeElement.value = '';
        }
        this.assigneeSearchCtrl.setValue(null);
    }

    displayUser(user: UserResponse): string {
        return user ? `${user.firstName} ${user.lastName}` : '';
    }

    toggleAccordion(): void {
        this.isAccordionOpen = !this.isAccordionOpen;
    }

    startSave(): void {
        this.isAccordionOpen = true;
        this.isSaving = true;
        this.filterNameControl.setValue('');
        setTimeout(() => {
            this.filterNameInput?.nativeElement?.focus();
        });
    }

    confirmSave(): void {
        const name = this.filterNameControl.value?.trim();
        if (!name) return;

        const filters = this.buildCurrentFilters();
        this.saveFilter.emit({ name, filters });
        this.isSaving = false;
        this.filterNameControl.setValue('');
    }

    cancelSave(): void {
        this.isSaving = false;
        this.filterNameControl.setValue('');
    }

    onLoadFilter(sf: SavedFilter): void {
        this.loadFilter.emit(sf);
    }

    onDeleteFilter(id: string, event: Event): void {
        event.stopPropagation();
        this.deleteFilter.emit(id);
    }

    private buildCurrentFilters(): FilterOptions {
        return {
            department: this.getSelectedValues('departments').join(',') || null,
            category: this.getSelectedValues('categories').join(',') || null,
            subCategory: this.getSelectedValues('subCategory').join(',') || null,
            status: this.getSelectedStatuses().join(',') || null,
            requester: this.selectedRequesters.map((u) => u._id).join(',') || null,
            createdAtFrom: this.filterForm.get('createdAtFrom')?.value,
            createdAtTo: this.filterForm.get('createdAtTo')?.value,
            slaDate: this.filterForm.get('slaDate')?.value,
            assignee: this.selectedAssignees.map((u) => u._id).join(',') || null,
            tags: this.selectedTags.join(',') || null,
            tagsFilterMode: this.tagsFilterMode,
        } as FilterOptions;
    }

    updateFilteredTagSuggestions(): void {
        this.filteredTagSuggestions = [];
        const current = this.tagSearchCtrl.value;
        if (current?.trim()) {
            this.tagInputFocus$.next(current);
        }
    }

    onTagInputFocus(): void {
        this.tagInputFocus$.next(this.tagSearchCtrl.value ?? '');
    }

    addTagFilter(event: MatChipInputEvent): void {
        const value = (event.value || '').trim();
        if (value && !this.selectedTags.includes(value)) {
            this.selectedTags.push(value);
            this.updateFilteredTagSuggestions();
            this._triggerFilterChange.next();
        }
        event.chipInput?.clear();
        this.tagSearchCtrl.setValue(null, { emitEvent: false });
    }

    removeTagFilter(tag: string): void {
        this.selectedTags = this.selectedTags.filter((t) => t !== tag);
        this.updateFilteredTagSuggestions();
        this._triggerFilterChange.next();
    }

    selectTagSuggestion(event: MatAutocompleteSelectedEvent): void {
        const value = event.option.viewValue;
        if (!this.selectedTags.includes(value)) {
            this.selectedTags.push(value);
            this.updateFilteredTagSuggestions();
            this._triggerFilterChange.next();
        }
        this.tagSearchCtrl.setValue(null, { emitEvent: false });
    }

    toggleTagsFilterMode(): void {
        this.tagsFilterMode = this.tagsFilterMode === 'OR' ? 'AND' : 'OR';
        if (this.selectedTags.length > 0) {
            this._triggerFilterChange.next();
        }
    }

    private setLocale(lang: string): void {
        if (lang === 'fr') {
            this.dateAdapter.setLocale('fr-FR');
        } else {
            this.dateAdapter.setLocale('en-US');
        }
    }
}
